// src/App.jsx
import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "./MainLayout.jsx";
import { RequireAuth, RequirePlanFeature } from "./auth/AuthContext.jsx";
import { isMarketingHostname } from "./lib/host.js";

// Screens “top”
const DashboardScreen = lazy(() => import("./DashboardScreen.jsx"));
const SalesScreen = lazy(() => import("./SalesScreen.jsx"));
const QuotesScreen = lazy(() => import("./QuotesScreen.jsx"));
const ProjectsScreen = lazy(() => import("./ProjectsScreen.jsx"));
const PresupuestosEspecialesScreen = lazy(() => import("./PresupuestosEspecialesScreen.jsx"));
const RemitosScreen = lazy(() => import("./RemitosScreen.jsx"));
const WorkOrderEditScreen = lazy(() => import("./WorkOrderEditScreen.jsx"));

// Módulos
const TrabajosScreen = lazy(() => import("./components/TrabajosScreen.jsx"));
const ClientsScreen = lazy(() => import("./components/ClientsScreen.jsx"));
const ProveedoresScreen = lazy(() => import("./components/ProveedoresScreen.jsx"));
const StockScreen = lazy(() => import("./components/StockScreen.jsx"));
const ContabilidadScreen = lazy(() => import("./components/ContabilidadScreen.jsx"));
const ComprasScreen = lazy(() => import("./components/compras/ComprasScreen.jsx"));
const OpcionesScreen = lazy(() => import("./components/OpcionesScreen.jsx"));
const MaquinasScreen = lazy(() => import("./components/MaquinasScreen.jsx"));
const SuperAdminScreen = lazy(() => import("./components/SuperAdminScreen.jsx"));
const LoginScreen = lazy(() => import("./LoginScreen.jsx"));
const UserProfileScreen = lazy(() => import("./UserProfileScreen.jsx"));
const SupportScreen = lazy(() => import("./SupportScreen.jsx"));
const MarketingLanding = lazy(() => import("./MarketingLanding.jsx"));

// Opciones (lazy)
const CotizacionDolarOpc = lazy(() => import("./components/opciones/CotizacionDolarOpc.jsx"));
const ProductosMostradorOpc = lazy(() => import("./components/opciones/ProductosMostradorOpc.jsx"));
const GeneralesOpc = lazy(() => import("./components/opciones/GeneralesOpc.jsx"));
const PapelesSustratosOpc = lazy(() => import("./components/opciones/PapelesSustratosOpc.jsx"));
const ConfigMaquinasOpc = lazy(() => import("./components/opciones/ConfigMaquinasOpc.jsx"));
const MaquinasOffsetOpc = lazy(() => import("./components/opciones/MaquinasOffsetOpc.jsx"));
const MaquinasDigitalOpc = lazy(() => import("./components/opciones/MaquinasDigitalOpc.jsx"));
const MaquinasPlotterOpc = lazy(() => import("./components/opciones/MaquinasPlotterOpc.jsx"));
const MaquinasFlexografiaOpc = lazy(() => import("./components/opciones/MaquinasFlexografiaOpc.jsx"));
const MaquinasAdicionalesOpc = lazy(() => import("./components/opciones/MaquinasAdicionalesOpc.jsx"));
const AdicionalesOpc = lazy(() => import("./components/opciones/AdicionalesOpc.jsx"));
const ConsumiblesOpc = lazy(() => import("./components/opciones/ConsumiblesOpc.jsx"));
const ProductosTerminadosOpc = lazy(() => import("./components/opciones/ProductosTerminadosOpc.jsx"));
const TipoClientesMargenesOpc = lazy(() => import("./components/opciones/TipoClientesMargenesOpc.jsx"));
const InfoEmpresaOpc = lazy(() => import("./components/opciones/InfoEmpresaOpc.jsx"));
const FormulariosOpc = lazy(() => import("./components/opciones/FormulariosOpc.tsx"));
const ComercialesOpc = lazy(() => import("./components/opciones/ComercialesOpc.jsx"));
const TiendaWebOpc = lazy(() => import("./components/opciones/TiendaWebOpc.jsx"));
const NotificacionesAlertasOpc = lazy(() => import("./components/opciones/NotificacionesAlertasOpc.jsx"));
const BotsOpc = lazy(() => import("./components/opciones/BotsOpc.jsx"));
const ArcaAfipOpc = lazy(() => import("./components/opciones/ArcaAfipOpc.jsx"));
const UsuariosOpc = lazy(() => import("./components/opciones/UsuariosOpc.jsx"));
const PapelesOffsetDigitalOpc = lazy(() => import("./components/opciones/PapelesOffsetDigitalOpc.jsx"));
const SustratosPlotterOpc = lazy(() => import("./components/opciones/SustratosPlotterOpc.jsx"));
const EmpleadosOpc = lazy(() => import("./components/opciones/EmpleadosOpc.jsx"));
const VendedoresOpc = lazy(() => import("./components/opciones/VendedoresOpc.jsx"));
const CondicionesComercialesOpc = lazy(() => import("./components/opciones/CondicionesComercialesOpc.jsx"));
const CajasBancosOpc = lazy(() => import("./components/opciones/CajasBancosOpc.jsx"));
const WithholdingsOpc = lazy(() => import("./components/opciones/WithholdingsOpc.jsx"));
const PerceptionsOpc = lazy(() => import("./components/opciones/PerceptionsOpc.jsx"));

// Contabilidad pantallas específicas
const AperturaCierreCaja = lazy(() => import("./components/contabilidad/AperturaCierreCaja.jsx"));
const OperacionContable = lazy(() => import("./components/contabilidad/OperacionContable.jsx"));
const ChequesScreen = lazy(() => import("./components/contabilidad/ChequesScreen.jsx"));
const CuentasTesoreria = lazy(() => import("./components/contabilidad/CuentasTesoreria.jsx"));
const MovimientosCuenta = lazy(() => import("./components/contabilidad/MovimientosCuenta.jsx"));
const PaymentOrdersScreen = lazy(() => import("./components/contabilidad/PaymentOrdersScreen.jsx"));
const PaymentOrderFormScreen = lazy(() => import("./components/contabilidad/PaymentOrderFormScreen.jsx"));
const PaymentOrderDetailScreen = lazy(() => import("./components/contabilidad/PaymentOrderDetailScreen.jsx"));
const FlujoCajaScreen = lazy(() => import("./components/contabilidad/FlujoCajaScreen.jsx"));
const PercepcionesScreen = lazy(() => import("./components/contabilidad/PercepcionesScreen.jsx"));
const RetencionesScreen = lazy(() => import("./components/contabilidad/RetencionesScreen.jsx"));
const LibroIvaScreen = lazy(() => import("./components/contabilidad/LibroIvaScreen.jsx"));
const FacturasPendientesScreen = lazy(() => import("./components/contabilidad/FacturasPendientesScreen.jsx"));


export default function App() {
  if (isMarketingHostname()) {
    return (
      <Suspense fallback={<div className="p-4">Cargando...</div>}>
        <Routes>
          <Route path="/super-admin" element={<SuperAdminScreen />} />
          <Route path="*" element={<MarketingLanding />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div className="p-4">Cargando…</div>}>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/super-admin" element={<SuperAdminScreen />} />
        <Route path="/" element={<RequireAuth><MainLayout /></RequireAuth>}>
          <Route index element={<DashboardScreen />} />
          <Route path="mi-perfil" element={<UserProfileScreen />} />
          <Route path="soporte" element={<SupportScreen />} />

          <Route path="ventas" element={<SalesScreen />} />
          <Route path="orden-trabajo/:id" element={<WorkOrderEditScreen />} />
          <Route path="trabajos" element={<TrabajosScreen />} />
          <Route path="presupuestos" element={<QuotesScreen />} />
          <Route path="proyectos" element={<ProjectsScreen />} />
          <Route path="presupuestos-especiales" element={<PresupuestosEspecialesScreen />} />
          <Route path="clientes" element={<ClientsScreen />} />
          <Route path="proveedores" element={<ProveedoresScreen />} />
          <Route path="stock" element={<StockScreen />} />
          <Route path="contabilidad" element={<ContabilidadScreen />} />
          <Route path="compras" element={<RequirePlanFeature feature="PURCHASES"><ComprasScreen /></RequirePlanFeature>} />
          <Route path="remitos" element={<RemitosScreen />} />

          <Route path="opciones" element={<OpcionesScreen />} />
          <Route path="maquinas" element={<MaquinasScreen />} />
          <Route path="opciones/CotizacionDolarOpc" element={<CotizacionDolarOpc />} />
          <Route path="opciones/PapelesOffsetDigitalOpc" element={<PapelesOffsetDigitalOpc />} />
          <Route path="opciones/SustratosPlotterOpc" element={<SustratosPlotterOpc />} />
          <Route path="opciones/ProductosMostradorOpc" element={<ProductosMostradorOpc />} />
          <Route path="opciones/GeneralesOpc" element={<GeneralesOpc />} />
          <Route path="opciones/GeneralesOpc/PermisosUsuarios" element={<GeneralesOpc />} />
          <Route path="opciones/GeneralesOpc/ArcaAfip" element={<RequirePlanFeature feature="ARCA"><GeneralesOpc /></RequirePlanFeature>} />
          <Route path="opciones/PapelesSustratosOpc" element={<PapelesSustratosOpc />} />
          <Route path="opciones/ConfigMaquinasOpc" element={<ConfigMaquinasOpc />} />
          <Route path="opciones/MaquinasOffsetOpc" element={<MaquinasOffsetOpc />} />
          <Route path="opciones/MaquinasDigitalOpc" element={<MaquinasDigitalOpc />} />
          <Route path="opciones/MaquinasPlotterOpc" element={<MaquinasPlotterOpc />} />
          <Route path="opciones/MaquinasFlexografiaOpc" element={<MaquinasFlexografiaOpc />} />
          <Route path="opciones/MaquinasAdicionalesOpc" element={<MaquinasAdicionalesOpc />} />
          <Route path="opciones/AdicionalesOpc" element={<AdicionalesOpc />} />
          <Route path="opciones/ConsumiblesOpc" element={<ConsumiblesOpc />} />
          <Route path="opciones/ProductosTerminadosOpc" element={<ProductosTerminadosOpc />} />
          <Route path="opciones/TipoClientesMargenesOpc" element={<TipoClientesMargenesOpc />} />
          <Route path="opciones/InfoEmpresaOpc" element={<InfoEmpresaOpc />} />
          <Route path="opciones/FormulariosOpc" element={<FormulariosOpc />} />
          <Route path="opciones/TiendaWebOpc" element={<TiendaWebOpc />} />
          <Route path="opciones/NotificacionesAlertasOpc" element={<NotificacionesAlertasOpc />} />
          <Route path="opciones/BotsOpc" element={<BotsOpc />} />
          <Route path="opciones/ArcaAfipOpc" element={<RequirePlanFeature feature="ARCA"><ArcaAfipOpc /></RequirePlanFeature>} />
          <Route path="opciones/UsuariosOpc" element={<UsuariosOpc />} />
          <Route path="opciones/EmpleadosOpc" element={<EmpleadosOpc />} />
          <Route path="opciones/VendedoresOpc" element={<VendedoresOpc />} />
          <Route path="opciones/condiciones-comerciales" element={<CondicionesComercialesOpc />} />
          <Route path="opciones/cajas-bancos" element={<CajasBancosOpc />} />
          <Route path="opciones/comerciales" element={<ComercialesOpc />} />
          <Route path="opciones/ComercialesOpc" element={<ComercialesOpc />} />
          <Route path="opciones/withholdings" element={<WithholdingsOpc />} />
          <Route path="opciones/perceptions" element={<PerceptionsOpc />} />

          <Route path="contabilidad/apertura-cierre" element={<AperturaCierreCaja />} />
          <Route path="contabilidad/operacion-contable" element={<OperacionContable />} />
          <Route path="contabilidad/cheques" element={<ChequesScreen />} />
          <Route path="contabilidad/cuentas" element={<CuentasTesoreria />} />
          <Route path="contabilidad/movimientos" element={<MovimientosCuenta />} />
          <Route path="contabilidad/flujo" element={<FlujoCajaScreen />} />
          <Route path="contabilidad/ordenes-pago" element={<PaymentOrdersScreen />} />
          <Route path="contabilidad/ordenes-pago/new" element={<PaymentOrderFormScreen />} />
          <Route path="contabilidad/ordenes-pago/:id" element={<PaymentOrderDetailScreen />} />
          <Route path="contabilidad/percepciones" element={<PercepcionesScreen />} />
          <Route path="contabilidad/retenciones" element={<RetencionesScreen />} />
          <Route path="contabilidad/libro-iva" element={<LibroIvaScreen />} />
          <Route path="contabilidad/facturas" element={<FacturasPendientesScreen />} />
          <Route path="contabilidad/sueldos" element={<EmpleadosOpc accountingMode />} />
          <Route path="contabilidad/comisiones" element={<VendedoresOpc accountingMode />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
